import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Circle, Flame } from 'lucide-react'
import { formatPrice, formatNumber } from '../../../utils/formatters'

/**
 * Список заданий эвента Rush Drop.
 * - binary = true → галочка «сделал / не сделал», шкала не рисуется;
 * - остальные → шкала progress/target (ширина = готовый percent);
 * - done = true → шкала перевыполнения (только при repeat_step > 1) или счётчик «×N»;
 * - metric = streak → показываем текущую и лучшую серию.
 * Текст задания не обрезаем — в нём правило зачёта.
 * Шкалы анимируют и уменьшение значения (прогресс может откатываться).
 */
const RushDropTaskCard = ({ task, showProgress }) => {
  const { t } = useTranslation()

  const done = Boolean(task.done)
  const isBinary = Boolean(task.binary)
  const isStreak = task.metric === 'streak'
  const unitCrystals = task.unit === 'crystals'
  const formatValue = (value) =>
    unitCrystals ? formatPrice(value) : formatNumber(Number(value || 0))

  const repeats = Number(task.repeats || 0)
  const repeatStep = Number(task.repeat_step || 1)
  const showRepeatBar = done && !isBinary && repeatStep > 1
  const showRepeatCounter = done && repeats > 0
  const nextBonus = Number(task.next_bonus || 0)

  return (
    <div
      className={`rounded-2xl border p-4 transition-colors ${
        done ? 'border-accent-primary/30 bg-accent-primary/5' : 'border-white/10 bg-surface'
      }`}
    >
      <div className="flex items-start gap-3">
        {done ? (
          <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-accent-primary" />
        ) : (
          <Circle size={20} className="mt-0.5 shrink-0 text-white/25" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug text-white">{task.title}</p>

          {showProgress && isStreak && (
            <p className="mt-1 text-xs text-text-secondary">
              {t('rushdrop.tasks.streak', 'Сейчас: {{current}} · Лучшая серия: {{best}}', {
                current: Number(task.streak_current || 0),
                best: Number(task.value || 0)
              })}
            </p>
          )}
        </div>

        {/* Счётчик перевыполнения — единственный вариант при repeat_step = 1 */}
        {showRepeatCounter && (
          <span className="flex shrink-0 items-center gap-1 rounded-md bg-accent-primary/15 px-2 py-0.5 text-xs font-bold text-accent-primary">
            <Flame size={12} />
            ×{repeats}
          </span>
        )}
      </div>

      {/* Основная шкала (кроме binary-заданий и анонса) */}
      {showProgress && !isBinary && (
        <div className="mt-3">
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                done
                  ? 'bg-gradient-to-r from-accent-primary to-accent-secondary'
                  : 'bg-accent-primary/70'
              }`}
              style={{ width: `${Math.min(100, Math.max(0, Number(task.percent) || 0))}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-xs text-text-secondary">
            <span className="tabular-nums">
              {formatValue(task.progress)} / {formatValue(task.target)}
            </span>
            {done && (
              <span className="font-semibold text-accent-primary">
                {t('rushdrop.tasks.done', 'Выполнено')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Вторая шкала перевыполнения — только при repeat_step > 1 */}
      {showProgress && showRepeatBar && (
        <div className="mt-3 border-t border-white/5 pt-3">
          <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-accent-secondary/70 transition-all duration-700"
              style={{ width: `${Math.min(100, Math.max(0, Number(task.repeat_percent) || 0))}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-xs text-text-secondary">
            <span className="tabular-nums">
              {formatValue(task.repeat_progress)} / {formatValue(repeatStep)}
            </span>
            {nextBonus > 0 && (
              <span className="text-accent-secondary">
                {t('rushdrop.tasks.nextBonus', { count: nextBonus, formatted: formatNumber(nextBonus) })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const RushDropTasks = ({ tasks, showProgress }) => {
  const { t } = useTranslation()
  const list = Array.isArray(tasks) ? tasks : []

  if (list.length === 0) return null

  return (
    <section>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-white">
          {t('rushdrop.tasks.title', 'Задания')}
        </h2>
        {showProgress && (
          <span className="text-xs text-text-secondary">
            {t('rushdrop.tasks.updateNote', 'Обновляется раз в минуту')}
          </span>
        )}
      </div>
      <div className="space-y-3">
        {list.map((task) => (
          <RushDropTaskCard key={task.id ?? task.key} task={task} showProgress={showProgress} />
        ))}
      </div>
    </section>
  )
}

export default RushDropTasks
